import { useEffect, useRef } from "react";
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from "react-router-dom";
import { getCanonicalUrl, getDefaultOgImage } from '@/lib/seoUtils';
import {
  Heart, Users, Shield, Award, Globe, CheckCircle,
  Target, Zap, BookOpen, TrendingUp, ArrowRight,
  Sparkles, Star,
} from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/* ── Scroll reveal ── */
function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add("visible"); observer.unobserve(el); } },
      { threshold: 0.12 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

/* ── Data ── */

const values = [
  {
    icon: Heart,
    title: "Passion for Learning",
    description: "We believe transformative education happens when the right student meets the right teacher. Every feature we build serves that belief.",
    gradient: "from-secondary to-[hsl(180,50%,42%)]",
  },
  {
    icon: Shield,
    title: "Trust & Verification",
    description: "Every teacher completes identity checks, credential verification, and a live teaching demo. You learn from mentors, not strangers.",
    gradient: "from-[hsl(260,60%,55%)] to-[hsl(280,55%,50%)]",
  },
  {
    icon: Zap,
    title: "AI-Powered Matching",
    description: "Our matching engine goes beyond keywords — it factors in learning style, pace, schedule, and goals to surface your ideal teacher.",
    gradient: "from-[hsl(35,95%,55%)] to-[hsl(25,90%,50%)]",
  },
  {
    icon: Globe,
    title: "Borderless Access",
    description: "Geography should never limit your potential. Our platform connects students and teachers across 30+ countries in real time.",
    gradient: "from-[hsl(340,70%,55%)] to-[hsl(310,60%,50%)]",
  },
  {
    icon: Users,
    title: "Community First",
    description: "From group class cohorts to peer forums, we build systems that turn individual sessions into lasting learning communities.",
    gradient: "from-[hsl(190,60%,45%)] to-[hsl(210,55%,40%)]",
  },
  {
    icon: TrendingUp,
    title: "Outcomes Over Hours",
    description: "We measure success in milestones reached, not sessions booked. Teachers are incentivised by student outcomes, not volume.",
    gradient: "from-[hsl(145,55%,42%)] to-[hsl(165,55%,38%)]",
  },
];


/* ── Component ── */
export default function AboutPage() {
  const heroRef = useReveal<HTMLDivElement>();
  const missionRef = useReveal<HTMLDivElement>();
  const valuesRef = useReveal<HTMLDivElement>();
  const ctaRef = useReveal<HTMLDivElement>();
  const { pathname } = useLocation();

  const title = 'About Us | BookMyCoaching';
  const description = 'BookMyCoaching connects students with verified tutors and coaching institutes across India.';
  const canonicalUrl = getCanonicalUrl(pathname);
  const ogImage = getDefaultOgImage();

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
      </Helmet>
      <Header />

      <main>
        {/* ── Hero ── */}
        <section className="relative pt-28 pb-20 sm:pt-44 sm:pb-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[hsl(230,55%,15%)] via-[hsl(210,55%,18%)] to-[hsl(190,45%,14%)]" />
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-secondary/15 rounded-full blur-[150px]" />
            <div className="absolute -bottom-40 -left-32 w-[500px] h-[500px] bg-[hsl(260,60%,50%)]/10 rounded-full blur-[130px]" />
          </div>
          <div className="absolute inset-0 opacity-[0.03]">
            <svg className="w-full h-full">
              <defs>
                <pattern id="about-grid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#about-grid)" />
            </svg>
          </div>

          <div ref={heroRef} className="container mx-auto px-4 relative z-10 text-center reveal">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-secondary mb-5 px-3 py-1 rounded-full bg-secondary/10 border border-secondary/20">
              <Sparkles className="w-3 h-3" />
              Our Story
            </span>
            <h1 className="font-sans text-4xl sm:text-5xl lg:text-6xl text-white mb-6 leading-tight">
              Built by Educators,{" "}
              <span className="bg-gradient-to-r from-secondary via-[hsl(200,70%,55%)] to-[hsl(260,60%,65%)] bg-clip-text text-transparent">
                Powered by Technology
              </span>
            </h1>
            <p className="text-lg text-white/60 max-w-2xl mx-auto leading-relaxed mb-10">
              BookMyCoaching exists because finding the right teacher should never be a matter of luck.
              We combine expert human curation with AI-driven matching to help every student find their perfect learning partner.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button asChild size="lg" className="rounded-full px-8 bg-gradient-to-r from-secondary to-[hsl(180,50%,42%)] text-white shadow-xl shadow-secondary/25 hover:-translate-y-0.5 hover:shadow-2xl transition-all">
                <Link to="/search">Find a Teacher</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full px-8 border-white/20 text-white hover:bg-white/10">
                <Link to="/teacher-profile-setup">Become a Teacher</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* ── Mission ── */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <div ref={missionRef} className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto reveal">
              {/* Left — text */}
              <div>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-secondary mb-4 px-3 py-1 rounded-full bg-secondary/10">
                  <Target className="w-3 h-3" />
                  Our Mission
                </span>
                <h2 className="font-sans text-3xl sm:text-4xl text-foreground mb-6 leading-snug">
                  Every learner deserves a teacher who truly gets them
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-5">
                  We started BookMyCoaching after watching too many students settle for the nearest available teacher — not the best-fit one. The result was frustration on both sides: students who didn't progress, teachers who couldn't showcase their real value.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-5">
                  Our answer was a platform that treats matching as a science. We collect signals about learning goals, pace, communication style, and subject depth — then surface teachers who match on all of them, not just availability.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Today, over 48,000 students have found their teaching match on BookMyCoaching. We're proudest not of the numbers, but of the stories behind them — the student who cleared JEE after two failed attempts, the professional who finally learned to code, the retiree who picked up a new language at 65.
                </p>
              </div>

              {/* Right — bento */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 rounded-2xl bg-gradient-to-br from-[hsl(165,55%,18%)] to-[hsl(210,55%,22%)] border border-secondary/20 p-7">
                  <BookOpen className="w-7 h-7 text-secondary mb-3" />
                  <p className="font-sans text-xl text-white mb-2">Learning is personal</p>
                  <p className="text-sm text-white/55 leading-relaxed">No two learners have the same goals, pace, or learning style. Our platform is built to celebrate that individuality, not flatten it.</p>
                </div>
                <div className="rounded-2xl bg-gradient-to-br from-[hsl(260,60%,22%)] to-[hsl(280,55%,25%)] border border-[hsl(260,60%,55%)]/25 p-6">
                  <CheckCircle className="w-6 h-6 text-[hsl(260,60%,65%)] mb-3" />
                  <p className="font-semibold text-white text-sm mb-1">Verified quality</p>
                  <p className="text-xs text-white/50 leading-relaxed">Every teacher is screened before going live</p>
                </div>
                <div className="rounded-2xl bg-gradient-to-br from-[hsl(35,90%,18%)] to-[hsl(25,85%,20%)] border border-[hsl(35,90%,55%)]/20 p-6">
                  <Star className="w-6 h-6 text-[hsl(35,95%,55%)] mb-3" />
                  <p className="font-semibold text-white text-sm mb-1">4.8/5 satisfaction</p>
                  <p className="text-xs text-white/50 leading-relaxed">Across 10,000+ verified reviews</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Values ── */}
        <section className="py-28 bg-background">
          <div className="container mx-auto px-4">
            <div ref={valuesRef} className="reveal">
              <div className="text-center mb-16 max-w-2xl mx-auto">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-secondary mb-4 px-3 py-1 rounded-full bg-secondary/10">
                  <Sparkles className="w-3 h-3" />
                  What We Stand For
                </span>
                <h2 className="font-sans text-3xl sm:text-4xl text-foreground mb-4">Our Principles</h2>
                <p className="text-muted-foreground">
                  Six beliefs that shape every product decision, partnership, and hire we make.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
                {values.map((v, i) => (
                  <div
                    key={i}
                    className="group rounded-2xl border border-border/40 bg-card p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className={cn(
                      "w-12 h-12 rounded-2xl flex items-center justify-center mb-5 bg-gradient-to-br shadow-lg",
                      v.gradient
                    )}>
                      <v.icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-semibold text-foreground text-base mb-2 group-hover:text-secondary transition-colors">{v.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{v.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="py-28 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[hsl(230,55%,15%)] via-primary to-[hsl(190,45%,16%)]" />
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute top-10 left-[10%] w-72 h-72 bg-secondary/15 rounded-full blur-[100px]" />
            <div className="absolute bottom-10 right-[10%] w-80 h-80 bg-[hsl(260,60%,50%)]/12 rounded-full blur-[100px]" />
          </div>

          <div ref={ctaRef} className="container mx-auto px-4 relative z-10 text-center reveal">
            <div className="max-w-2xl mx-auto">
              <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-white/15 text-primary-foreground text-sm font-medium mb-8 backdrop-blur-md">
                <Sparkles className="w-3.5 h-3.5 text-[hsl(35,95%,55%)]" />
                Join BookMyCoaching
              </span>
              <h2 className="font-sans text-4xl sm:text-5xl text-white mb-6">
                Be part of something{" "}
                <span className="bg-gradient-to-r from-secondary via-[hsl(200,70%,55%)] to-[hsl(260,60%,65%)] bg-clip-text text-transparent">
                  meaningful
                </span>
              </h2>
              <p className="text-primary-foreground/60 text-lg mb-10 leading-relaxed">
                Whether you're a student chasing a goal or a teacher ready to share your expertise — there's a place for you here.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="rounded-full px-10 py-6 bg-gradient-to-r from-secondary to-[hsl(180,50%,42%)] text-white shadow-xl shadow-secondary/25 hover:-translate-y-0.5 hover:shadow-2xl transition-all">
                  <Link to="/search">
                    Find Your Teacher
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full px-10 py-6 border-white/20 text-white hover:bg-white/10">
                  <Link to="/teacher-profile-setup">Apply as a Teacher</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
